import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { NavigationError, provideRouter, TitleStrategy, withInMemoryScrolling, withNavigationErrorHandler } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ErrorHandler, importProvidersFrom, inject, Injector, provideAppInitializer } from '@angular/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { LayoutModule } from '@angular/cdk/layout';
import { AppComponent } from './app/app.component';
import { AppTranslationModule } from './app/core/translation.module';
import { AppMatPaginatorIntl } from './app/shared/data-grid/paginator-translation';
import { routes } from './app/app.routes';
import { I18nTitleStrategy } from './app/core/i18n-title-strategy';
import { devModeInterceptor } from './app/features/dev-tool/dev-tool.interceptor';
import { tenantContextInterceptor } from '@core/tenant-context.interceptor';
import { authInterceptor } from '@core/auth.interceptor';
import { apiBaseUrlInterceptor } from '@core/api-base-url.interceptor';
import { portalAccessDeniedInterceptor } from '@core/portal-access-denied.interceptor';
import { loadingOverlayInterceptor } from './app/shared/loading/loading-overlay.interceptor';
import { OktaAuthModule, provideOktaAuth } from '@core/okta.config';
import { TokenReceiverService } from '@core/token-receiver.service';
import { AppTranslationService } from '@core/translation.service';
import { GlobalErrorHandler } from '@core/errors/global-error-handler';
import { cacheInterceptor } from '@core/cache/cache.interceptor';
import { captureAlliantPortalOrigin } from '@core/capture-alliant-portal-origin';
import { STORAGE_TOKEN } from '@core/storage.token';
import { WINDOW } from '@core/window.token';
import { ConsentService } from '@core/services/consent.service';
import { FeatureFlagsService } from '@core/services/feature-flags.service';
import { ENV } from '@core/env.token';
import { RECAPTCHA_LOADER_OPTIONS, RECAPTCHA_V3_SITE_KEY, RecaptchaLoaderService, ReCaptchaV3Service } from 'ng-recaptcha-2';

bootstrapApplication(AppComponent, {
  providers: [
    provideAppInitializer(() => inject(ConsentService).initialize()),

    provideAppInitializer(async () => {
      const injector = inject(Injector);
      (await import('./datadog')).bootstrapDatadog(injector);
    }),

    ReCaptchaV3Service,
    RecaptchaLoaderService,
    { provide: RECAPTCHA_V3_SITE_KEY, useFactory: () => inject(ENV).reCaptchaSiteKey },
    {
      provide: RECAPTCHA_LOADER_OPTIONS,
      useValue: {
        onBeforeLoad() {
          const url = new URL('https://www.recaptcha.net/recaptcha/enterprise.js');
          url.searchParams.set('hl', 'en');

          return { url };
        },
        onLoaded(recaptcha: unknown) {
          return (recaptcha as { enterprise: unknown }).enterprise;
        },
      },
    },

    importProvidersFrom(BrowserModule, AppTranslationModule, OktaAuthModule, LayoutModule),
    provideOktaAuth(),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
      withNavigationErrorHandler((e: NavigationError) => inject(ErrorHandler).handleError(e.error))
    ),
    provideHttpClient(
      withInterceptors([
        loadingOverlayInterceptor,
        tenantContextInterceptor,
        authInterceptor,
        apiBaseUrlInterceptor,
        portalAccessDeniedInterceptor,
        devModeInterceptor,
        cacheInterceptor,
      ])
    ),
    provideAppInitializer(async () => {
      const { provideLuxonDateAdapter } = await import('@angular/material-luxon-adapter');
      return provideLuxonDateAdapter();
    }),
    { provide: MatPaginatorIntl, useClass: AppMatPaginatorIntl },
    { provide: TitleStrategy, useClass: I18nTitleStrategy },
    provideAppInitializer(() => inject(TokenReceiverService).receiveTokensFromHash()),
    provideAppInitializer(() => inject(FeatureFlagsService).loadFlags()),
    provideAppInitializer(() => {
      const translationService = inject(AppTranslationService);
      return translationService.initializeTranslations();
    }),
    provideAppInitializer(() => captureAlliantPortalOrigin(inject(STORAGE_TOKEN), inject(WINDOW))),

    { provide: ErrorHandler, useClass: GlobalErrorHandler },
  ],
}).catch((err) => console.error(err));
